import React from 'react';
import { PropTypes } from '../../../utils/propTypes';
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
  EmptyContent,
} from '../Empty';
import { Icon } from '../Icon';
import styles from './LoadingState.module.css';

/**
 * FailedState — LoadingState's static sibling for "the work failed"
 * heroes (agent crash, fetch error, missing resource). Same Empty
 * composition and variants, but a destructive-tinted warning icon
 * instead of a spinner, no delay (errors should appear instantly),
 * and an optional `action` slot for a retry / back button.
 *
 *   <FailedState
 *     title="Analysis Failed"
 *     description={error}
 *     action={<Button …>Back to Projects</Button>}
 *     variant="page"                 // 'page' | 'inline'
 *   />
 */
const FailedState = ({
  title = 'Something went wrong',
  description = null,
  action = null,
  variant = 'inline',
  className = '',
}) => (
  <div
    className={`${styles.root} ${variant === 'page' ? styles.page : styles.inline} ${className}`.trim()}
    data-slot="failed-state"
  >
    <Empty>
      <EmptyHeader>
        <EmptyMedia variant="icon" className={styles.failedIcon}>
          <Icon name="warning" size={20} label="Error" />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        {description && <EmptyDescription>{description}</EmptyDescription>}
      </EmptyHeader>
      {action && <EmptyContent>{action}</EmptyContent>}
    </Empty>
  </div>
);

FailedState.propTypes = {
  title: PropTypes.node,
  description: PropTypes.node,
  action: PropTypes.node,
  variant: PropTypes.oneOf(['page', 'inline']),
  className: PropTypes.string,
};

export default FailedState;
export { FailedState };
